// src/modules/shared/utils/formatters.ts

export { handleDateTime, formatApiDateTime } from "./handle-date.utils";

export const getPrimaryImage = (images: string[]): string =>
  images.find((img) => img.trim().length > 0)?.trim() ?? "/no-image.jpg";

export const truncateText = (text: string, maxLength: number = 120): string => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
};

// ===== Product-specific formatters =====
export const parseCurrencyValue = (value: string | number): number => {
  if (typeof value === "number") return value;
  const raw = value.trim();
  if (!raw) return NaN;
  let cleaned = raw.replace(/[\s_]/g, "");
  const dotCount = (cleaned.match(/\./g) || []).length;
  if (cleaned.includes(",")) cleaned = cleaned.replace(/,/g, "");
  if (dotCount > 1) cleaned = cleaned.replace(/\./g, "");
  return Number(cleaned);
};

export const formatMoney = (
  price: string | number,
  priceUnit: string,
): string => {
  const numericPrice = parseCurrencyValue(price);
  if (Number.isFinite(numericPrice)) {
    const formatter = getCurrencyFormatter(priceUnit);
    return `${formatter.format(numericPrice)} ${priceUnit}`.trim();
  }
  return `${String(price)} ${priceUnit}`.trim();
};

export const convertPriceByUnit = (
  price: string | number,
  fromUnit: string,
  toUnit: string,
): string => {
  const numPrice = parseCurrencyValue(price);
  if (isNaN(numPrice)) return "0";
  if (fromUnit === toUnit) return String(numPrice);
  if (fromUnit === "VND" && toUnit === "USD") {
    return (numPrice / 25000).toFixed(2); // EXCHANGE_RATE_VND_TO_USD
  }
  if (fromUnit === "USD" && toUnit === "VND") {
    return Math.round(numPrice * 25000).toString();
  }
  return String(numPrice);
};

export const getPriceInputProps = (unit: string) =>
  unit === "VND" ? { min: 0, step: 1000 } : { min: 0, step: 0.01 };

export const applyDiscount = (
  price: string | number,
  percentage: number,
): number | null => {
  const numericPrice = parseCurrencyValue(price);
  if (!Number.isFinite(numericPrice)) return null;
  const safePercent = Number.isFinite(percentage)
    ? Math.min(Math.max(percentage, 0), 100)
    : 0;
  return Math.max(numericPrice - (numericPrice * safePercent) / 100, 0);
};

export const stringifyOptions = (
  options: { key: string; values: string[] }[],
): string =>
  options.map((opt) => `${opt.key}: ${opt.values.join(", ")}`).join("\n");

export const parseOptionText = (
  value: string,
): { key: string; values: string[] }[] =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [rawKey, rawValues = ""] = line.split(":");
      const key = rawKey?.trim();
      if (!key) return null;
      const values = rawValues
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      return { key, values };
    })
    .filter((item): item is { key: string; values: string[] } => Boolean(item));

export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case "TRACKER":
    case "UNLIMITED":
      return "bg-soft-success text-success";
    case "PRE_ORDER":
      return "bg-soft-warning text-warning";
    case "OUT_OF_STOCK":
      return "bg-soft-danger text-danger";
    default:
      return "bg-soft-secondary text-secondary";
  }
};

/**
 * Chuyển đổi giá trị enum từ tiếng Anh (BE) sang chuỗi hiển thị bằng tiếng Việt.
 * Nếu không có ánh xạ, fallback về viết hoa chữ cái đầu và thay dấu gạch dưới.
 */
export const formatEnumLabel = (value: string): string => {
  const mapping: Record<string, string> = {
    // ProductStatus
    TRACKER: "Theo dõi",
    UNLIMITED: "Không giới hạn",
    PRE_ORDER: "Đặt trước",
    OUT_OF_STOCK: "Hết hàng",
    // PaymentMethod
    CONTACT: "Liên hệ",
    PAYOS: "PayOS",
    // WarrantyUnit
    DAY: "Ngày",
    MONTH: "Tháng",
    YEAR: "Năm",
  };

  const upperValue = value.toUpperCase();
  if (mapping[upperValue]) {
    return mapping[upperValue];
  }

  // Fallback: viết hoa chữ cái đầu và thay dấu gạch dưới
  return value
    .toLowerCase()
    .split("_")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

// helper nội bộ
const currencyFormatters = {
  VND: new Intl.NumberFormat("vi-VN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }),
  USD: new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
};

const getCurrencyFormatter = (unit: string) => {
  const normalized = unit.toUpperCase();
  if (normalized === "VND" || normalized === "VNĐ")
    return currencyFormatters.VND;
  if (normalized === "USD") return currencyFormatters.USD;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};
